import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { withTheme } from "@emotion/react";
import Chart from "react-chartjs-2";
import { rgba } from "polished";

import {
  Card as MuiCard,
  CardContent,
  CardHeader,
  Button as MuiButton,
  CircularProgress,
  Box,
} from "@mui/material";
import { Loop as LoopIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";
import { getBarChartData } from "src/services/generalService";

const Card = styled(MuiCard)(spacing);

const SmallButton = styled(MuiButton)`
  padding: 4px;
  min-width: 0;

  svg {
    width: 0.9em;
    height: 0.9em;
  }
`;

const ChartWrapper = styled.div`
  height: 340px;
  width: 100%;
`;

const LoaderWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 340px;
`;

/**
 * Reusable Bar Chart component that fetches data from an API
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Card title
 * @param {string} props.apiUrl - API endpoint to fetch data from
 * @param {Object} props.theme - Theme object from withTheme HOC
 * @param {number} props.mb - Margin bottom from MUI spacing (default: 6)
 */
function BarChartCard({
  title = "Chart",
  apiUrl,
  theme,
  mb = 6,
}) {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getBarChartData(apiUrl);

      if (response?.status !== 200 || !response?.data) {
        throw new Error(
          response?.message || `API Error: ${response?.status || "Unknown"}`
        );
      }

      const data = response?.data;
      const firstDatasetColor = theme.palette.secondary.main;
      const secondDatasetColor = rgba(theme.palette.secondary.main, 0.33);

      // Format chart data
      const formattedChartData = {
        labels: data.labels || [],
        datasets: [
          {
            label: data.dataset1Label || "Mobile",
            backgroundColor: data.dataset1Color || firstDatasetColor,
            borderColor: data.dataset1Color || firstDatasetColor,
            hoverBackgroundColor: data.dataset1Color || firstDatasetColor,
            hoverBorderColor: data.dataset1Color || firstDatasetColor,
            data: data.dataset1Data || [],
            barPercentage: 0.5,
            categoryPercentage: 0.5,
          },
          {
            label: data.dataset2Label || "Desktop",
            backgroundColor: data.dataset2Color || secondDatasetColor,
            borderColor: data.dataset2Color || secondDatasetColor,
            hoverBackgroundColor: data.dataset2Color || secondDatasetColor,
            hoverBorderColor: data.dataset2Color || secondDatasetColor,
            data: data.dataset2Data || [],
            barPercentage: 0.5,
            categoryPercentage: 0.5,
            borderRadius: 6,
          },
        ],
      };

      setChartData(formattedChartData);
    } catch (err) {
      console.error("Error fetching bar chart data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  useEffect(() => {
    if (apiUrl) {
      fetchData();
    }
  }, [apiUrl, theme]);

  const defaultOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        grid: {
          display: false,
        },
        stacked: true,
        ticks: {
          stepSize: 20,
          fontColor: theme.palette.text.secondary,
        },
      },
      x: {
        stacked: true,
        grid: {
          color: "transparent",
        },
        ticks: {
          fontColor: theme.palette.text.secondary,
        },
      },
    },
  };

  const isLoading = loading;

  return (
    <Card mb={mb}>
      <CardHeader
        action={
          <SmallButton
            size="small"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <LoopIcon />
          </SmallButton>
        }
        title={title}
      />
      <CardContent>
        {isLoading ? (
          <LoaderWrapper>
            <CircularProgress />
          </LoaderWrapper>
        ) : error ? (
          <LoaderWrapper>
            <div>Error loading chart: {error}</div>
          </LoaderWrapper>
        ) : chartData ? (
          <ChartWrapper>
            <Chart type="bar" data={chartData} options={defaultOptions} />
          </ChartWrapper>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default withTheme(BarChartCard);
